import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../BasicComponents/Button';
import type { ConfirmDialogProps, ConfirmDialogState, ConfirmDialogVariant } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),' +
  'select:not([disabled]),[tabindex]:not([tabindex="-1"])';

const VARIANT_ICON_COLOR: Record<ConfirmDialogVariant, string> = {
  danger: 'text-danger-500',
  warning: 'text-warning-500',
};

const CONFIRM_BUTTON_CLASS: Record<ConfirmDialogVariant, string> = {
  danger: '',
  warning:
    'bg-warning-500 text-white border-transparent hover:bg-warning-600 active:bg-warning-700 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-500 focus-visible:ring-offset-2',
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function DangerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── Focus trap ───────────────────────────────────────────────────────────────

function trapFocus(panelEl: HTMLElement, event: KeyboardEvent) {
  const focusable = Array.from(panelEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null,
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

// ─── ConfirmDialog ────────────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
  confirmDisabled = false,
  children,
}: ConfirmDialogProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [state, setState] = useState<ConfirmDialogState>('closed');

  // Open/close state machine with animation
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setState('opening');
      const t = setTimeout(() => setState('open'), 16);
      return () => clearTimeout(t);
    } else {
      if (state === 'closed') return;
      setState('closing');
      const t = setTimeout(() => {
        setState('closed');
        if (triggerRef.current && 'focus' in triggerRef.current) {
          (triggerRef.current as HTMLElement).focus();
        }
      }, 150);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus Cancel button when open (safer default per spec)
  useEffect(() => {
    if (state === 'open') {
      cancelRef.current?.focus();
    }
  }, [state]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(panelRef.current, e.nativeEvent);
      }
    },
    [onCancel],
  );

  if (state === 'closed') return null;

  const backdropOpacity =
    state === 'opening' || state === 'closing' ? 'opacity-0' : 'opacity-100';
  const panelVisible = state === 'open';
  const panelTransform = panelVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95';

  const iconColorClass = VARIANT_ICON_COLOR[variant];
  const confirmBtnClass = CONFIRM_BUTTON_CLASS[variant];
  const Icon = variant === 'danger' ? DangerIcon : WarningIcon;

  return (
    <div
      className="fixed inset-0 z-[50] overflow-y-auto"
      aria-hidden={state !== 'open'}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-200 ${backdropOpacity}`}
        aria-hidden="true"
      />

      {/* Centering container */}
      <div className="relative flex min-h-full items-center justify-center p-4 sm:p-6">
        {/* Panel */}
        <div
          ref={panelRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          tabIndex={-1}
          className={[
            'relative flex flex-col bg-white dark:bg-neutral-900 shadow-lg outline-none',
            'rounded-lg w-full sm:max-w-[400px] mx-auto',
            `transition-all duration-200 ${panelTransform}`,
            'z-[60]',
          ].join(' ')}
        >
          {/* Body */}
          <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center gap-3">
            <span className={`flex items-center justify-center h-12 w-12 ${iconColorClass}`}>
              <Icon className="h-10 w-10" />
            </span>
            <h2
              id={titleId}
              className="text-base font-semibold text-neutral-900 dark:text-neutral-100"
            >
              {title}
            </h2>
            <p id={descId} className="text-sm text-neutral-600 dark:text-neutral-400">
              {description}
            </p>
            {children}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-neutral-200 dark:border-neutral-700 px-6 py-4 shrink-0">
            <Button
              ref={cancelRef}
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            {variant === 'danger' ? (
              <Button variant="danger" onClick={onConfirm} loading={loading} disabled={confirmDisabled}>
                {confirmLabel}
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={onConfirm}
                loading={loading}
                disabled={confirmDisabled}
                className={confirmBtnClass}
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
